<template>
    <modal id="finance-transaction-modal"
           size="large"
           v-model="showModal"
           :title="generateModalTitle('transaction')"
           @submit="submitData" :loading="loading"
           :preloader="preloader">

        <form
            ref="form"
            :data-url='selectedUrl ? selectedUrl : `${TENANT_BASE_URL}app/finance-transactions`'
            @submit.prevent="submitData"
        >
            <div class="row">
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('type')"
                        list-value-field="name"
                        v-model="formData.type"
                        :chooseLabel="$t('type')"
                        :required="true"
                        :error-message="$errorMessage(errors, 'type')"
                        :options="typeList"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('category')"
                        list-value-field="name"
                        v-model="formData.category_id"
                        :chooseLabel="$t('category')"
                        :required="true"
                        :error-message="$errorMessage(errors, 'category_id')"
                        :options="filteredCategories"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('source_account')"
                        list-value-field="name"
                        v-model="formData.source_account_id"
                        :chooseLabel="$t('source_account')"
                        :required="true"
                        :error-message="$errorMessage(errors, 'source_account_id')"
                        :fetch-url="`${TENANT_BASE_URL}app/finance-accounts?all=1`"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group
                        type="date"
                        :label="$t('date')"
                        :placeholder="$placeholder('date')"
                        v-model="formData.process_date"
                        :required="true"
                        :error-message="$errorMessage(errors, 'process_date')">
                    </app-form-group>
                </div>
                <div class="col-md-6">
                    <div class="form-group mb-3">
                        <label>{{ $t('amount') + ' (' + $t('tax_included') + ')' }}</label>
                        <div class="input-group amount-tax-selector">
                            <input
                                type="number"
                                class="form-control"
                                v-model="formData.amount"
                                :placeholder="$placeholder('amount')"
                                :class="{'is-invalid': $errorMessage(errors, 'amount')}"
                                min="0"
                                step="0.01"
                            >
                            <div class="input-group-append">
                                <select
                                    class="custom-select tax-rate-select"
                                    v-model="taxRateSelection"
                                >
                                    <option value="0">{{ $t('exempt') }}</option>
                                    <option v-for="tax in taxOptions"
                                            :key="tax.id"
                                            :value="String(tax.id)">
                                        {{ tax.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div v-if="$errorMessage(errors, 'amount')" class="invalid-feedback d-block">
                            {{ $errorMessage(errors, 'amount') }}
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <app-form-group
                        type="decimal"
                        :label="$t('tax')"
                        :placeholder="$placeholder('tax')"
                        v-model="formData.tax"
                        :class="{'is-invalid': $errorMessage(errors, 'tax')}"
                        :error-message="$errorMessage(errors, 'tax')"
                        min="0"
                        step="0.01"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group
                        type="text"
                        :label="$t('reference_code')"
                        :placeholder="$placeholder('reference_code')"
                        v-model="formData.reference_code"
                        :error-message="$errorMessage(errors, 'reference_code')">
                    </app-form-group>
                </div>
                <div class="col-md-6">
                    <app-form-group
                        type="text"
                        :label="$t('remark')"
                        :placeholder="$textAreaPlaceHolder('remark')"
                        v-model="formData.remark"
                        :text-area-rows="4"
                        :error-message="$errorMessage(errors, 'remark')">
                    </app-form-group>
                </div>

                <div class="col-md-12">
                    <app-form-group-selectable
                        type="multi-select"
                        :label="$t('tags')"
                        list-value-field="title"
                        v-model="formData.tags"
                        :chooseLabel="$t('tag')"
                        :error-message="$errorMessage(errors, 'tags')"
                        :fetch-url="`${TENANT_BASE_URL}app/interview-tag?per_page=0&type=3`"
                    />
                </div>

                <div class="col-md-12">
                    <div class="form-group">
                        <label>{{ $t('attachments') }}</label>
                        <app-input
                            type="dropzone"
                            v-model="files"
                            :error-message="$errorMessage(errors, 'file')"
                        />
                        <small class="text-muted font-italic mt-3 d-inline-block">
                            {{ $t('document_recommendation') }}
                        </small>
                    </div>

                    <div class="form-group" v-if="current_files.length">
                        <label>{{ $t('current_files') }}</label>
                        <div class="img-container">
                            <div class="img-box" v-for="(url, index) in current_files" :key="'current-image-' + index">
                                <img v-if="isImage(url)"
                                    :src="url"
                                    class="img-thumbnail"
                                    :title="getFileName(url)"
                                />
                                <div v-else
                                    :href="url"
                                    target="_blank"
                                    class="d-flex align-items-center justify-content-center text-truncate img-thumbnail"
                                    :title="getFileName(url)"
                                >
                                    <i :class="`fa fa-file-${getFileExtension(url).substring(1)}`"></i>
                                </div>
                                <div class="img-action">
                                    <button type="button" class="btn btn-sm btn-primary" @click="viewFile(url)" :title="$t('view')">
                                        <i class="fas fa-eye"></i>
                                    </button>
                                    <button type="button" class="btn btn-sm btn-danger" @click="deleteFile(index)" :title="$t('delete')">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </modal>
</template>

<script>
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {formDataAssigner} from "../../../../common/Helper/Support/FormHelper";
import {formatDateForServer, today} from "../../../../common/Helper/Support/DateTimeHelper";
import {axiosGet} from "../../../../common/Helper/AxiosHelper";
import {mapState} from "vuex";

export default {
    name: "FinanceTransactionCreate",
    props: {
        defaultType: {
            type: String,
            default: ''
        },
        defaultSourceAccountId: {
            type: [String, Number],
            default: ''
        }
    },
    mixins: [FormHelperMixins, ModalMixin],
    data() {
        const initialType = this.defaultType;
        const initialSourceAccountId = this.defaultSourceAccountId;
        return {
            TENANT_BASE_URL,
            locale: window?.appLanguage.toLowerCase() || 'ja',
            initialType,
            initialSourceAccountId,
            files   : [],
            current_files: [],
            deleted_files: [],
            categories: [],
            taxOptions: [],
            typeList: [
                {id: 'income', name: this.$t('income')},
                {id: 'expense', name: this.$t('expense')}
            ],
            taxRateSelection: 0,
            formData: {
                id: null,
                category_id: '',
                tags: [],
                source_account_id: initialSourceAccountId,
                type: initialType,
                amount: '',
                tax: '',
                reference_code: '',
                process_date: today(),
                remark: ''
            }
        }
    },
    computed: {
        ...mapState({
            languages: state => state.support.languages
        }),
        filteredCategories() {
            if (!this.formData.type) {
                return this.categories;
            }
            return this.categories.filter(category => category.type === this.formData.type);
        },
    },
    methods: {
        resetForm() {
            this.formData = {
                id: null,
                category_id: '',
                tags: [],
                source_account_id: this.initialSourceAccountId,
                type: this.initialType,
                amount: '',
                tax: '',
                reference_code: '',
                process_date: today(),
                remark: ''
            };
            this.taxRateSelection = '';
            this.errors = {};
        },
        getFinanceTransaction() {
            this.$axios.get(this.selectedUrl)
                .then(response => {
                    this.formData = response.data;
                    this.formData.tax = response.data.tax !== null ? String(response.data.tax) : '';
                    const derivedRate = this.deriveRateFromValues(this.formData.amount, this.formData.tax);
                    this.taxRateSelection = derivedRate !== null ? String(derivedRate) : '';
                })
                .catch(error => {
                    this.$toastError(error.message);
            });
        },
        fetchTaxRates() {
            axiosGet(`${this.TENANT_BASE_URL}app/finance/list-tax`)
                .then(({data}) => {
                    this.taxOptions = data;
                })
                .catch(error => {
                    this.$toastError(error.message);
                });
        },
        loadCategories() {
            axiosGet(`${this.TENANT_BASE_URL}app/finance-categories?all=1&locale=${this.locale}`)
                .then(({data}) => {
                    this.categories = data.map(category => {
                        return {
                            ...category,
                            name: this.$displayByLocale(category.name)
                        }
                    });
                })
                .catch(error => {
                    this.$toastError(error.message);
                });
        },
        syncTaxFromRate() {
            const amount = parseFloat(this.formData.amount);
            const rate = parseFloat(this.taxRateSelection);

            if (isNaN(amount) || isNaN(rate)) {
                this.formData.tax = '';
                return;
            }

            const taxAmount = (amount * rate) / 100;
            this.formData.tax = Math.round(taxAmount * 100) / 100;
        },
        deriveRateFromValues(amount, tax) {
            const amt = parseFloat(amount);
            const tx = parseFloat(tax);
            if (isNaN(amt) || amt <= 0 || isNaN(tx) || tx <= 0) {
                return null;
            }
            const rate = (tx / amt) * 100;
            return Math.round(rate * 100) / 100;
        },
        submitData() {
            // Client-side validation for amount
            if (parseFloat(this.formData.amount) <= 0) {
                this.$toastr.e(this.$t('amount_must_be_greater_than_zero'));
                return;
            }

            let url      = this.$refs.form.dataset['url'];
            let formData = formDataAssigner(new FormData, this.formData);
            if (this.formData.tags) {
                this.formData.tags.forEach(tag_id => formData.append('tag_id[]', tag_id));
            }
            formData.process_date = formData.process_date ? formatDateForServer(formData.process_date) : '';
            this.files.forEach(file => formData.append('files[]', file));
            this.deleted_files.forEach(fileUrl => formData.append('deleted_files[]', fileUrl))

            if (this.selectedUrl) {
                // for file update need to send by post.
                formData.append('_method', 'PATCH');
            }

            this.loading = true;
            this.submitFromFixin('post', url, formData);
        },
        afterSuccess({data}) {
            this.formData = {};
            $('#finance-transaction-modal').modal('hide');
            this.$emit('input', false);
            this.toastAndReload(data.message, 'finance-transaction-table');
            this.$hub.$emit('reload-finance-account-table');
        },
        afterSuccessFromGetEditData(response) {
            this.preloader = false;
            this.current_files = response.data.files || [];
            delete response.data.files;
            this.formData = response.data;
            this.formData.tags = response.data.tags ? response.data.tags.map(tag => tag.id) : [];
            this.formData.tax = response.data.tax !== null ? String(response.data.tax) : '';
            const derivedRate = this.deriveRateFromValues(this.formData.amount, this.formData.tax);
            this.taxRateSelection = derivedRate !== null ? String(derivedRate) : '';
        },
    },
    watch: {
        'formData.type'(newType, oldType) {
            // igrore initial assignment
            if (oldType === '') {
                return;
            }

            if (newType !== oldType) {
                this.formData.category_id = '';
            }
        },
        taxRateSelection() {
            this.syncTaxFromRate();
        },
        'formData.amount'() {
            this.syncTaxFromRate();
        }
    },
    created() {
        this.loadCategories();
        this.fetchTaxRates();
    }
}
</script>

<style scoped lang="scss">
    ::v-deep .amount-tax-selector {
        .form-control {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }
    
        .tax-rate-select {
            width: 110px;
            min-width: 90px;
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
            background-image: url(/images/chevron-down.svg) !important;
            background-position: right 0.5rem center !important;
        }
        
        .input-group-append {
            margin-left: 0px;
        }
    }
</style>
